<template>
  <a-page
    :title="$t('whats_new')"
    :image_source="image_source"
    :image_height="image_height"
  >
    <div class="vr-wrapper">
      <!-- HERO DE LA VERSIÓN ACTUAL -->
      <v-card class="vr-hero mb-6" flat v-if="current_release">
        <div class="vr-hero__bg"></div>
        <div class="vr-hero__content">
          <v-row align="center" no-gutters>
            <v-col cols="12" md="7">
              <div class="vr-hero__badge">
                {{ $t("release_whats_new_in") }}
              </div>
              <div class="d-flex align-baseline flex-wrap">
                <span class="vr-hero__version">{{
                  current_release.version
                }}</span>
                <span class="vr-hero__date ml-4">
                  {{ current_release.date | formatDateFull }}
                </span>
              </div>
              <h2 class="vr-hero__title">{{ $t(current_release.title) }}</h2>
              <p
                class="vr-hero__desc"
                v-html="$t(current_release.description)"
              ></p>
              <div class="vr-hero__stats">
                <!-- Los contadores dependen de los permisos, que llegan con la
                     API. Hasta entonces no los pintamos: un "0 novedades"
                     mientras carga parece un error. -->
                <div class="vr-hero__stat" v-if="visible_features.length">
                  <span class="vr-hero__stat-value">{{
                    visible_features.length
                  }}</span>
                  <span class="vr-hero__stat-label">{{
                    $t("release_stat_features")
                  }}</span>
                </div>
                <div class="vr-hero__stat" v-if="new_screens_count">
                  <span class="vr-hero__stat-value">{{
                    new_screens_count
                  }}</span>
                  <span class="vr-hero__stat-label">{{
                    $t("release_stat_new_screens")
                  }}</span>
                </div>
                <div
                  class="vr-hero__stat"
                  v-if="
                    current_release.minor_items &&
                      current_release.minor_items.length
                  "
                >
                  <span class="vr-hero__stat-value">{{
                    current_release.minor_items.length
                  }}</span>
                  <span class="vr-hero__stat-label">{{
                    $t("release_stat_improvements")
                  }}</span>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="hidden-sm-and-down">
              <a-version-screenshot
                :image="current_release.cover"
                :version="current_release.version"
                icon="mdi-rocket-launch-outline"
                :alt="$t(current_release.title)"
                @zoom="openZoom"
              />
            </v-col>
          </v-row>
        </div>
      </v-card>

      <!-- ÍNDICE RÁPIDO -->
      <div class="vr-index mb-4" v-if="visible_features.length > 1">
        <v-chip
          v-for="f in visible_features"
          :key="'idx-' + f.id"
          small
          outlined
          class="mr-2 mb-2 vr-index__chip"
          @click="scrollToFeature(f)"
        >
          <v-icon left size="15">{{ f.icon }}</v-icon>
          {{ $t(f.title) }}
        </v-chip>
      </div>

      <!-- FEATURES -->
      <div v-if="visible_features.length">
        <template v-for="(f, index) in visible_features">
          <a-version-feature
            :key="f.id"
            :feature="f"
            :version="current_release.version"
            :reversed="index % 2 === 1"
            @zoom="openZoom"
          />
          <v-divider
            :key="'d-' + f.id"
            v-if="index < visible_features.length - 1"
            class="vr-divider"
          ></v-divider>
        </template>
      </div>

      <!-- OTRAS MEJORAS -->
      <v-card
        class="vr-minor mt-8"
        flat
        v-if="current_release && current_release.minor_items"
      >
        <div class="vr-minor__title">
          <v-icon size="18" class="mr-2" color="primary">
            mdi-format-list-checks
          </v-icon>
          {{ $t("release_other_improvements") }}
        </div>
        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="i in current_release.minor_items"
            :key="i"
            class="vr-minor__item"
          >
            <v-icon size="15" class="mr-2" color="green">mdi-plus</v-icon>
            <span v-html="$t(i)"></span>
          </v-col>
        </v-row>
      </v-card>

      <!-- HISTORIAL -->
      <div class="vr-history mt-10" v-if="previous_releases.length">
        <div class="vr-history__title">
          <v-icon size="18" class="mr-2" color="grey darken-1">
            mdi-history
          </v-icon>
          {{ $t("release_history") }}
        </div>
        <v-expansion-panels flat accordion class="vr-history__panels">
          <v-expansion-panel v-for="r in previous_releases" :key="r.version">
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <span class="vr-history__version">{{ r.version }}</span>
                <div class="ml-4">
                  <div class="vr-history__name">{{ $t(r.title) }}</div>
                  <div class="vr-history__date">
                    {{ r.date | formatDate }}
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="vr-history__desc" v-html="$t(r.description)"></p>
              <ul class="vr-history__list" v-if="r.minor_items">
                <li v-for="i in r.minor_items" :key="i" v-html="$t(i)"></li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>

    <!-- ZOOM DE CAPTURA -->
    <v-dialog v-model="zoom" max-width="1400" @click:outside="zoom = false">
      <v-card flat class="vr-zoom">
        <v-btn icon class="vr-zoom__close" @click="zoom = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <img :src="zoom_src" class="vr-zoom__img" v-if="zoom_src" />
      </v-card>
    </v-dialog>
  </a-page>
</template>

<script>
import { mapGetters } from "vuex";
import aPage from "@/components/aPage";
import aVersionFeature from "@/components/version/aVersionFeature";
import aVersionScreenshot from "@/components/version/aVersionScreenshot";
import { prefetchCurrentRelease } from "@/components/version/releaseImages.js";

export default {
  components: { aPage, aVersionFeature, aVersionScreenshot },
  data: () => ({
    image_source: "/img/header.jpg", //Mandatory path
    image_height: 65, //Mandatory number,
    zoom: false,
    zoom_src: null
  }),
  computed: {
    contentStyle() {
      // Mandatory
      var margin_top = this.image_height - 40;
      return "position:relative; margin-top:-" + margin_top + "px";
    },
    visible_features() {
      return this.current_release && this.current_release.features
        ? this.current_release.features
        : [];
    },
    new_screens_count() {
      return this.visible_features.filter(
        f => (f.tag || "release_tag_new") === "release_tag_new"
      ).length;
    },
    ...mapGetters({
      current_release: "current_release",
      previous_releases: "previous_releases"
    })
  },
  methods: {
    openZoom(src) {
      this.zoom_src = src;
      this.zoom = true;
    },
    scrollToFeature(feature) {
      const el = document.getElementById("feature-" + feature.id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  },
  created() {
    // Las tarjetas no se pintan hasta que la API devuelve hoteles y permisos.
    // Lanzamos las miniaturas ya para que viajen en paralelo con esas llamadas
    // y estén en caché cuando el componente monte.
    prefetchCurrentRelease();

    // Al visitar la pantalla damos por leídas las novedades de esta versión.
    this.$store.dispatch("markReleaseAsSeen");
  }
};
</script>

<style scoped>
.vr-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 28px;
  padding-bottom: 40px;
}

/* HERO */
.vr-hero {
  position: relative;
  border-radius: 16px !important;
  overflow: hidden;
  background: transparent !important;
}
.vr-hero__bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(120deg, #2e21bd 0%, #1576c7 55%, #21b5db 100%);
}
.vr-hero__content {
  position: relative;
  padding: 34px 36px;
  color: #ffffff;
}
.vr-hero__badge {
  display: inline-block;
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.16);
  border-radius: 30px;
  padding: 4px 14px;
  margin-bottom: 14px;
}
.vr-hero__version {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
}
.vr-hero__date {
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.8;
}
.vr-hero__title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 14px;
  margin-bottom: 8px;
}
.vr-hero__desc {
  font-size: 0.96rem;
  line-height: 1.6;
  opacity: 0.92;
  max-width: 560px;
  margin-bottom: 18px;
}
.vr-hero__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
}
.vr-hero__stat {
  display: flex;
  flex-direction: column;
}
.vr-hero__stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
}
.vr-hero__stat-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.8;
  margin-top: 4px;
}

/* ÍNDICE */
.vr-index__chip {
  cursor: pointer;
}

/* DIVIDER */
.vr-divider {
  border-color: rgba(0, 0, 0, 0.05) !important;
}

/* OTRAS MEJORAS */
.vr-minor {
  background: #f6f7fb !important;
  border-radius: 14px !important;
  padding: 22px 26px;
}
.vr-minor__title {
  display: flex;
  align-items: center;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.78rem;
  letter-spacing: 0.1em;
  color: #1f2540;
  margin-bottom: 14px;
}
.vr-minor__item {
  display: flex;
  align-items: flex-start;
  font-size: 0.89rem;
  color: #3d4360;
  line-height: 1.5;
  padding: 5px 16px 5px 0;
}

/* HISTORIAL */
.vr-history__title {
  display: flex;
  align-items: center;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.78rem;
  letter-spacing: 0.1em;
  color: #6b7189;
  margin-bottom: 6px;
}
.vr-history__panels {
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
}
.vr-history__version {
  font-size: 1.4rem;
  font-weight: 800;
  color: #c3c7d6;
  min-width: 56px;
}
.vr-history__name {
  font-weight: 600;
  color: #1f2540;
  font-size: 0.95rem;
}
.vr-history__date {
  font-size: 0.75rem;
  color: #8a90a6;
  text-transform: uppercase;
}
.vr-history__desc {
  color: #5b6180;
  font-size: 0.9rem;
}
.vr-history__list {
  color: #3d4360;
  font-size: 0.88rem;
  line-height: 1.7;
}

/* ZOOM */
.vr-zoom {
  position: relative;
  background: #ffffff !important;
}
.vr-zoom__close {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
}
.vr-zoom__img {
  display: block;
  width: 100%;
  height: auto;
}

@media (max-width: 960px) {
  .vr-hero__content {
    padding: 26px 22px;
  }
  .vr-hero__version {
    font-size: 3rem;
  }
}
</style>
